import Rollbar, { Configuration } from 'rollbar';

const ROLLBAR_ACCESS_TOKEN =
  process.env.ROLLBAR_SERVER_ACCESS_TOKEN || process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_ACCESS_TOKEN;
const BUILD_ENV = process.env.NEXT_PUBLIC_BUILD_ENV;
const BUILD_ID = process.env.NEXT_PUBLIC_BUILD_ID;

// Each pattern below is a real production error confirmed to originate outside our own code —
// third-party scripts (browser extensions, in-app browsers, accessibility widgets) interfering
// with the page — see JPA-8230/JPA-8231/JPA-8232 for the per-error investigation.
const IGNORED_ERROR_PATTERNS = [
  // JPA-8230: a browser extension monkey-patches appendChild and tries to JSON.stringify a DOM
  // node that React has attached its (circular) internal fiber object to.
  /Converting circular structure to JSON/i,
  // JPA-8231: Facebook's in-app browser injects its own script into every page it opens; on some
  // Android WebView versions that injected script fails to parse.
  /^Unexpected token '\{'$/,
  // JPA-8232: a script that mutates the DOM outside React's control (e.g. the UserWay
  // accessibility widget) removes/moves a node React is mid-way through repositioning.
  /Failed to execute 'insertBefore' on 'Node'/i,
  // JPA-8239: Safari's wording for the same insertBefore/removeChild DOM-race as JPA-8232 —
  // something outside React removed/moved a node React still thinks is there.
  /The object can not be found here/i,
  // JPA-8240: UserWay's own widget script fails inserting its stylesheet — the whole stack trace
  // is inside cdn.userway.org's file, none of it is ours.
  /Cannot read properties of null \(reading 'prepend'\)/i,
  // JPA-8245: a Chrome extension's own executor script (chrome-extension://... in the stack,
  // zero of our files) reads a field named "M_ID" off something undefined.
  /Cannot read properties of undefined \(reading 'M_ID'\)/i,
  // JPA-8246: UserWay's widget uses a regex feature (named capture groups) unsupported by
  // older Safari versions; the whole stack trace is inside cdn.userway.org's file. Matched on
  // the suffix only — Rollbar's browser SDK truncates the "Invalid regular expression: " prefix
  // off SyntaxError messages before this reaches body.trace.exception.message (confirmed via a
  // live staging occurrence during QA), so anchoring on the full raw message never matches.
  /invalid group specifier name/i,
  // JPA-8247: UserWay's own tunings fetch failed ("Failed to fetch"), which left their internal
  // state uninitialized, and their own init code then crashed reading off that missing state —
  // confirmed via their own logged "Tunings Error"/"Initialization error" lines right before this.
  /Cannot read properties of undefined \(reading 'hasOwnProperty'\)/i,
];

export const getRollbarErrorMessage = (item: Rollbar.Dictionary): string =>
  (item as any)?.body?.trace?.exception?.message || (item as any)?.body?.trace_chain?.[0]?.exception?.message || '';

// JPA-7375: third-party scripts we embed (the UserWay accessibility widget) and browser
// extensions throw inside their own minified code — we cannot fix those, and the errors say
// nothing about our app. The tell is that EVERY frame of the stack sits in a file we do not
// own. Matching on the origin instead of the message keeps our own crashes reportable, even
// when they share the wording (e.g. Safari's "The operation is insecure." on blocked storage).
const THIRD_PARTY_FRAME_PATTERNS = [
  /^https?:\/\/cdn\.userway\.org\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,
  /^safari-(web-)?extension:\/\//i,
];

const getRollbarFrames = (item: Rollbar.Dictionary): { filename?: string }[] =>
  (item as any)?.body?.trace?.frames || (item as any)?.body?.trace_chain?.[0]?.frames || [];

const isThirdPartyOnlyStack = (item: Rollbar.Dictionary): boolean => {
  const frames = getRollbarFrames(item);

  return (
    frames.length > 0 &&
    frames.every((frame) => THIRD_PARTY_FRAME_PATTERNS.some((pattern) => pattern.test(frame?.filename || '')))
  );
};

// JPA-8248: a failed <link>/<script>/<img> resource load fires a DOM `error` event that
// Rollbar's global uncaught-error listener mistakes for a JS exception. The resulting item
// carries no useful message (often just "{}"), so message matching can't catch it — the only
// tell is that one of the raw arguments is the DOM element itself rather than an Error.
const isDomElementArg = (arg: unknown): boolean =>
  typeof arg === 'object' && arg !== null && typeof (arg as { tagName?: unknown }).tagName === 'string';

export const shouldIgnoreRollbarError = (item: Rollbar.Dictionary, args: Rollbar.LogArgument[] = []): boolean => {
  if (args.some(isDomElementArg)) return true;
  if (isThirdPartyOnlyStack(item)) return true;
  const message = getRollbarErrorMessage(item);
  return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

const rollbarConfig: Configuration = {
  accessToken: ROLLBAR_ACCESS_TOKEN,
  enabled: Boolean(ROLLBAR_ACCESS_TOKEN),
  captureUncaught: true,
  captureUnhandledRejections: true,
  checkIgnore: (_isUncaught, args, item) => shouldIgnoreRollbarError(item, args),
  payload: {
    environment: BUILD_ENV,
    client: {
      javascript: {
        source_map_enabled: true,
        code_version: BUILD_ID,
        guess_uncaught_frames: true,
      },
    },
  },
};

export const rollbar = ROLLBAR_ACCESS_TOKEN ? new Rollbar(rollbarConfig) : null;
